import { Component, type ErrorInfo, type ReactNode } from 'react'

interface SilentErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
}

interface SilentErrorBoundaryState {
  hasError: boolean
}

/**
 * React error boundary that silently swallows errors without reporting them to Sentry.
 *
 * Use this component when wrapping third-party or user-supplied content (e.g. embedded
 * HTML, iframes, widgets, partner scripts) whose runtime errors are outside our control
 * and should not pollute the error monitoring dashboard.
 *
 * Unlike {@link https://docs.sentry.io/platforms/javascript/guides/nextjs/features/error-boundary/ Sentry.ErrorBoundary},
 * this boundary does NOT call `Sentry.captureException`. Errors are logged to the console
 * in development mode only; in production they are discarded entirely.
 *
 * The primary purpose is to isolate untrusted subtrees so that a render error in a
 * third-party widget does not crash the entire page or trigger a false-positive
 * Sentry alert for on-call.
 *
 * @example
 * <SilentErrorBoundary fallback={<span>Widget unavailable</span>}>
 *   <PartnerBanner />
 * </SilentErrorBoundary>
 *
 * @example
 * // Without fallback — renders null on error
 * <SilentErrorBoundary>
 *   <DangerousElement markup={courseData.embeddedCode} />
 * </SilentErrorBoundary>
 */
export class SilentErrorBoundary extends Component<
  SilentErrorBoundaryProps,
  SilentErrorBoundaryState
> {
  constructor(props: SilentErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(): SilentErrorBoundaryState {
    return { hasError: true }
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    if (process.env.NODE_ENV === 'development') {
      console.error('[SilentErrorBoundary]', error, errorInfo)
    }
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      return this.props.fallback ?? null
    }

    return this.props.children
  }
}
